import { useCallback, useEffect, useState } from 'react';
import type { CaseReport, FinanceFlowReport, ReportKind } from '@foundation/shared';
import { CASE_STATUS_LABEL_TH, REPORT_KINDS, REPORT_KIND_LABEL_TH } from '@foundation/shared';
import { api, apiBlob, buildQuery } from '../lib/api';
import { formatSatang } from '../lib/format';
import { ErrorAlert, Loading } from '../components/ui';
import { useAuth } from '../auth';

const GROUP_LABEL: Record<string, string> = {
  month: 'รายเดือน',
  campaign: 'ตามแคมเปญ',
  aid_type: 'ตามประเภทความช่วยเหลือ',
  area: 'ตามพื้นที่',
};

export default function Reports() {
  const { allowed } = useAuth();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [groupBy, setGroupBy] = useState<'month' | 'campaign' | 'aid_type' | 'area'>('month');

  const [flow, setFlow] = useState<FinanceFlowReport | null>(null);
  const [cases, setCases] = useState<CaseReport | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [downloading, setDownloading] = useState<ReportKind | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const query = buildQuery({ from: from || undefined, to: to || undefined, groupBy });
      const [f, c] = await Promise.all([
        api<FinanceFlowReport>(`/reports/finance-flow${query}`),
        api<CaseReport>(`/reports/cases${query}`),
      ]);
      setFlow(f);
      setCases(c);
    } catch (e) {
      setError(e);
    }
  }, [from, to, groupBy]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * ดาวน์โหลด CSV ผ่าน fetch แล้วสร้าง blob เอง
   * เปิด URL ตรง ๆ ไม่ได้เพราะ endpoint ต้องการ Authorization header
   */
  async function download(kind: ReportKind) {
    setDownloading(kind);
    setError(null);
    try {
      const { blob, rowCount, truncated } = await apiBlob(
        `/reports/export${buildQuery({ kind, from: from || undefined, to: to || undefined })}`,
      );

      // บอกเสมอเมื่อข้อมูลถูกตัด — ไฟล์ที่ขาดไปเงียบ ๆ อันตรายกว่าไฟล์ที่ไม่ได้ไฟล์
      if (truncated) {
        window.alert(
          `ข้อมูลถูกตัดที่ ${rowCount} แถว — กรุณาระบุช่วงวันที่ให้แคบลงแล้วดาวน์โหลดใหม่`,
        );
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${REPORT_KIND_LABEL_TH[kind]}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e);
    } finally {
      setDownloading(null);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>รายงาน</h1>
          <p>เงินเข้า-เงินออก ความคืบหน้าของเคส และการดึงข้อมูลออกเป็นไฟล์</p>
        </div>
      </div>

      <ErrorAlert error={error} />

      <div className="card">
        <div className="toolbar">
          <div className="field">
            <label htmlFor="rfrom">ตั้งแต่</label>
            <input id="rfrom" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="rto">ถึง</label>
            <input id="rto" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="rgroup">แบ่งกลุ่ม</label>
            <select
              id="rgroup"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as typeof groupBy)}
            >
              {Object.entries(GROUP_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {!flow ? (
        <Loading />
      ) : (
        <>
          <div className="grid grid-4">
            <div className="card">
              <div className="stat-label">เงินบริจาคที่ตรวจสอบแล้ว</div>
              <div className="stat-value">{formatSatang(flow.totals.inflow)}</div>
              <div className="stat-sub">นับตามวันที่โอนจริง</div>
            </div>
            <div className="card">
              <div className="stat-label">จ่ายช่วยเหลือจริง</div>
              <div className="stat-value">{formatSatang(flow.totals.outflow)}</div>
              <div className="stat-sub">ไม่ใช่ยอดที่อนุมัติ</div>
            </div>
            <div className="card">
              <div className="stat-label">ยังไม่ระบุปลายทาง</div>
              <div className="stat-value">{formatSatang(flow.totals.unallocated)}</div>
              <div className="stat-sub">เงินเข้าที่ยังไม่ผูกกับเคส</div>
            </div>
            <div className="card">
              <div className="stat-label">ผู้ได้รับความช่วยเหลือ</div>
              <div className="stat-value">{flow.totals.beneficiariesHelped}</div>
              <div className="stat-sub">นับตัวคน ไม่ใช่จำนวนครั้ง</div>
            </div>
          </div>

          <div className="card">
            <h2>เงินเข้า-เงินออก · {GROUP_LABEL[flow.groupBy]}</h2>
            {flow.rows.length === 0 ? (
              <p className="muted">ไม่มีข้อมูลในช่วงที่เลือก</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{flow.groupBy === 'month' ? 'เดือน' : 'กลุ่ม'}</th>
                      <th>เงินเข้า</th>
                      <th>เงินออก</th>
                      <th>ส่วนต่าง</th>
                      <th>รายการบริจาค</th>
                      <th>รายการจ่าย</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flow.rows.map((r) => (
                      <tr key={r.key}>
                        <td>{r.label || r.key}</td>
                        <td>{formatSatang(r.inflow)}</td>
                        <td>{formatSatang(r.outflow)}</td>
                        <td className={r.inflow - r.outflow < 0 ? 'badge red' : ''}>
                          {formatSatang(r.inflow - r.outflow)}
                        </td>
                        <td className="small muted">{r.donationCount}</td>
                        <td className="small muted">{r.disbursementCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {flow.groupBy !== 'month' && flow.groupBy !== 'campaign' && (
              <p className="hint">
                เงินบริจาคไม่ได้ผูกกับพื้นที่หรือประเภทความช่วยเหลือโดยตรง
                มุมมองนี้จึงแสดงเฉพาะฝั่งเงินออก
              </p>
            )}
          </div>
        </>
      )}

      {cases && (
        <div className="grid grid-2">
          <div className="card">
            <h2>เคสตามสถานะ</h2>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>สถานะ</th>
                    <th>จำนวน</th>
                    <th>ยอดที่อนุมัติ</th>
                  </tr>
                </thead>
                <tbody>
                  {cases.byStatus.map((r) => (
                    <tr key={r.status}>
                      <td>{CASE_STATUS_LABEL_TH[r.status]}</td>
                      <td>{r.count}</td>
                      <td>{formatSatang(r.approvedAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <dl className="detail-list">
              <dt>รออนุมัติระดับ 1</dt>
              <dd>{cases.pendingApproval.level1}</dd>
              <dt>รออนุมัติระดับ 2</dt>
              <dd>{cases.pendingApproval.level2}</dd>
              <dt>รออนุมัติระดับ 3</dt>
              <dd>{cases.pendingApproval.level3}</dd>
              <dt>เลยกำหนด</dt>
              <dd>
                {cases.overdueCount > 0 ? (
                  <span className="badge red">{cases.overdueCount} เคส</span>
                ) : (
                  '—'
                )}
              </dd>
              <dt>เวลาเฉลี่ยที่ใช้อนุมัติ</dt>
              <dd>
                {cases.averageDaysToApprove === null
                  ? '—'
                  : `${cases.averageDaysToApprove} วัน`}
              </dd>
            </dl>
          </div>

          <div className="card">
            <h2>ความช่วยเหลือตามประเภทและพื้นที่</h2>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>ประเภท</th>
                    <th>เคส</th>
                    <th>จ่ายแล้ว</th>
                  </tr>
                </thead>
                <tbody>
                  {cases.byAidType.map((r) => (
                    <tr key={r.aidTypeId}>
                      <td>{r.name}</td>
                      <td>{r.count}</td>
                      <td>{formatSatang(r.disbursedAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>พื้นที่</th>
                    <th>เคส</th>
                    <th>จ่ายแล้ว</th>
                  </tr>
                </thead>
                <tbody>
                  {cases.byArea.map((r) => (
                    <tr key={r.areaId ?? 'none'}>
                      <td>{r.name}</td>
                      <td>{r.count}</td>
                      <td>{formatSatang(r.disbursedAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {allowed('report:export') && (
        <div className="card">
          <h2>ดาวน์โหลดข้อมูล</h2>
          <p className="hint">
            ไฟล์ CSV เปิดใน Excel ภาษาไทยได้ทันที · ทุกการดาวน์โหลดถูกบันทึกไว้ใน audit log
            ตาม PDPA และไม่มีคอลัมน์เลขบัตรประชาชน
          </p>
          <div className="btn-row">
            {REPORT_KINDS.map((k) => (
              <button
                key={k}
                className="btn ghost"
                disabled={downloading !== null}
                onClick={() => void download(k)}
              >
                {downloading === k ? 'กำลังสร้างไฟล์…' : REPORT_KIND_LABEL_TH[k]}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
